import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { ThemeService } from '../../services/theme.service';

@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive],
  template: `
    <nav class="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border shadow-sm">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex items-center justify-between h-16 lg:h-20">
          <!-- Logo -->
          <a routerLink="/" class="flex items-center gap-2 lg:gap-3 hover:opacity-80 transition-opacity">
            <div class="w-10 h-10 lg:w-12 lg:h-12 rounded-xl bg-primary flex items-center justify-center">
              <svg class="w-5 h-5 lg:w-6 lg:h-6 text-primary-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12h1m2 0h14M6 12a2 2 0 11-4 0 2 2 0 014 0zm12 0a2 2 0 11-4 0 2 2 0 014 0zM9 8h2m2 8h2"/>
              </svg>
            </div>
            <div class="flex flex-col">
              <span class="text-lg lg:text-xl font-bold text-primary">پارس فیتنس</span>
              <span class="text-[10px] lg:text-xs text-muted-foreground">PARAS FITNESS</span>
            </div>
          </a>

          <!-- Desktop Navigation -->
          <div class="hidden lg:flex items-center gap-1">
            <div *ngFor="let item of menuItems" class="relative group">
              <a
                [routerLink]="item.link"
                routerLinkActive="bg-primary !text-primary-foreground"
                class="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors text-foreground hover:bg-accent hover:text-accent-foreground"
              >
                <span [innerHTML]="item.icon"></span>
                {{ item.label }}
                <span *ngIf="item.subItems.length > 0" class="mr-1">
                  <svg class="w-3.5 h-3.5 transition-transform group-hover:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                  </svg>
                </span>
              </a>
              <!-- Submenu -->
              <div *ngIf="item.subItems.length > 0" class="absolute top-full right-0 mt-1 w-48 py-2 bg-popover border border-border rounded-xl shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                <a
                  *ngFor="let sub of item.subItems"
                  [routerLink]="sub.link"
                  class="block w-full text-right px-4 py-2.5 text-sm text-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
                >
                  {{ sub.label }}
                </a>
              </div>
            </div>
          </div>

          <!-- Right side -->
          <div class="flex items-center gap-2">
            <!-- Theme toggle -->
            <button (click)="toggleTheme()" class="p-2 rounded-full hover:bg-accent transition-colors">
              <svg *ngIf="!isDark" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"/>
              </svg>
              <svg *ngIf="isDark" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"/>
              </svg>
            </button>

            <!-- Mobile menu button -->
            <button (click)="mobileMenuOpen = !mobileMenuOpen" class="lg:hidden p-2 rounded-full hover:bg-accent">
              <svg *ngIf="!mobileMenuOpen" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"/>
              </svg>
              <svg *ngIf="mobileMenuOpen" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>
      </div>

      <!-- Mobile Menu -->
      <div *ngIf="mobileMenuOpen" class="lg:hidden border-t border-border bg-background/95 backdrop-blur-md">
        <div class="px-4 py-3 space-y-1">
          <div *ngFor="let item of menuItems">
            <a
              [routerLink]="item.link"
              (click)="mobileMenuOpen = false"
              class="flex items-center justify-between w-full px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-foreground hover:bg-accent"
            >
              <div class="flex items-center gap-2">
                <span [innerHTML]="item.icon"></span>
                {{ item.label }}
              </div>
            </a>
            <div *ngIf="item.subItems.length > 0" class="pr-8 py-1 space-y-0.5">
              <a
                *ngFor="let sub of item.subItems"
                [routerLink]="sub.link"
                (click)="mobileMenuOpen = false"
                class="block w-full text-right px-3 py-2 rounded-lg text-sm text-muted-foreground hover:bg-accent transition-colors"
              >
                {{ sub.label }}
              </a>
            </div>
          </div>
        </div>
      </div>
    </nav>
  `,
  styles: [`
    :host { display: block; }
  `]
})
export class NavbarComponent implements OnInit {
  isDark = true;
  mobileMenuOpen = false;

  menuItems = [
    {
      label: 'صفحه اصلی',
      link: '/',
      icon: '<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/></svg>',
      subItems: []
    },
    {
      label: 'درباره ما',
      link: '/about',
      icon: '<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>',
      subItems: [
        { label: 'تاریخچه باشگاه', link: '/about' },
        { label: 'چشم انداز و ماموریت', link: '/about' },
        { label: 'افتخارات', link: '/about' },
      ]
    },
    {
      label: 'دوره های آموزشی',
      link: '/classes',
      icon: '<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/></svg>',
      subItems: [
        { label: 'بدنسازی', link: '/classes' },
        { label: 'یوگا و پیلاتس', link: '/classes' },
        { label: 'آیروبیک', link: '/classes' },
        { label: 'مبارزه و رزمی', link: '/classes' },
        { label: 'فیتنس', link: '/classes' },
      ]
    },
    {
      label: 'محصولات',
      link: '/products',
      icon: '<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"/></svg>',
      subItems: [
        { label: 'مکمل های ورزشی', link: '/products' },
        { label: 'لوازم ورزشی', link: '/products' },
        { label: 'پوشاک ورزشی', link: '/products' },
        { label: 'تجهیزات تمرین', link: '/products' },
      ]
    },
    {
      label: 'گالری تصاویر',
      link: '/gallery',
      icon: '<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>',
      subItems: []
    },
    {
      label: 'مربیان',
      link: '/trainers',
      icon: '<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/></svg>',
      subItems: [
        { label: 'مربیان بدنسازی', link: '/trainers' },
        { label: 'مربیان یوگا', link: '/trainers' },
        { label: 'مربیان رزمی', link: '/trainers' },
      ]
    },
    {
      label: 'تماس با ما',
      link: '/contact',
      icon: '<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>',
      subItems: []
    },
  ];

  constructor(private themeService: ThemeService) {}

  ngOnInit(): void {
    this.themeService.darkMode$.subscribe(isDark => this.isDark = isDark);
  }

  toggleTheme(): void {
    this.themeService.toggleTheme();
  }
}
